import { apiClient } from '../infra/http/apiClient'

class VideoService {
  /**
   * Upload a video file with optional cover image
   * @param {File} videoFile - Video file to upload (MP4, MOV, AVI, WEBM, max 500MB)
   * @param {File} [coverImage] - Optional cover image (JPEG, PNG, max 10MB)
   * @param {string} [customFileName] - Optional custom file name (without extension)
   * @returns {Promise} Promise object containing uploaded video metadata
   */
  static async uploadVideo(videoFile, coverImage, customFileName = null) {
    try {
      // Validate video file type
      const allowedVideoTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm']
      if (!allowedVideoTypes.includes(videoFile.type)) {
        throw new Error('Invalid video file type. Only MP4, MOV, AVI, and WEBM are allowed.')
      }

      // Validate video file size (500MB = 500 * 1024 * 1024 bytes)
      const maxVideoSize = 500 * 1024 * 1024
      if (videoFile.size > maxVideoSize) {
        throw new Error('Video file size exceeds 500MB limit.')
      }

      // Validate cover image if provided
      if (coverImage) {
        const allowedImageTypes = ['image/jpeg', 'image/png']
        if (!allowedImageTypes.includes(coverImage.type)) {
          throw new Error('Invalid cover image type. Only JPEG and PNG are allowed.')
        }

        const maxImageSize = 10 * 1024 * 1024
        if (coverImage.size > maxImageSize) {
          throw new Error('Cover image size exceeds 10MB limit.')
        }
      }

      // Prepare files for upload
      let videoToUpload = videoFile
      if (customFileName) {
        const videoExtension = videoFile.name.split('.').pop() || videoFile.type.split('/')[1] || 'mp4'
        const newVideoName = `${customFileName}.${videoExtension}`
        videoToUpload = new File([videoFile], newVideoName, { type: videoFile.type })
      }

      // Create FormData
      const formData = new FormData()
      formData.append('video', videoToUpload)

      if (coverImage) {
        let coverToUpload = coverImage
        if (customFileName) {
          const imageExtension = coverImage.name.split('.').pop() || coverImage.type.split('/')[1] || 'jpg'
          const newCoverName = `${customFileName}-cover.${imageExtension}`
          coverToUpload = new File([coverImage], newCoverName, { type: coverImage.type })
        }
        formData.append('coverImage', coverToUpload)
      }

      // Upload files
      const response = await apiClient.post('/videos', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      return response.data
    } catch (error) {
      console.error('Failed to upload video:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Delete a video
   * @param {string} videoId - Video resource ID
   * @returns {Promise} Promise object containing deletion result
   */
  static async deleteVideo(videoId) {
    try {
      const response = await apiClient.delete(`/video/${videoId}`)
      return response.data
    } catch (error) {
      console.error('Failed to delete video:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Get video metadata
   * @param {string} videoId - Video resource ID
   * @returns {Promise} Promise object containing video metadata
   */
  static async getVideo(videoId) {
    try {
      const response = await apiClient.get(`/video/${videoId}`)
      return response.data
    } catch (error) {
      console.error('Failed to fetch video:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Get list of videos
   * @param {Object} filters - Optional filters
   * @param {string} [filters.ownerId] - Filter by owner user ID (admin/instructor only)
   * @param {number} [filters.page=0] - Page number
   * @param {number} [filters.size=20] - Page size
   * @returns {Promise} Promise object containing video list
   */
  static async getVideos(filters = {}) {
    try {
      const response = await apiClient.get('/videos', {
        params: filters,
      })
      return response.data
    } catch (error) {
      console.error('Failed to fetch videos:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Delete multiple videos
   * @param {string[]} videoIds - Array of video resource IDs to delete (max 50)
   */
  static async deleteMultipleVideos(videoIds) {
    try {
      if (!Array.isArray(videoIds) || videoIds.length === 0) {
        throw new Error('Video IDs array is required and cannot be empty.')
      }
      if (videoIds.length > 50) {
        throw new Error('Cannot delete more than 50 videos at once.')
      }

      const response = await apiClient.post('/videos/delete-multiple', {
        resourceIds: videoIds,
      })
      return response.data
    } catch (error) {
      console.error('Failed to delete multiple videos:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Handle API errors and convert to user-friendly messages
   * @private
   */
  static handleError(error) {
    if (error.response) {
      const { status, data } = error.response
      switch (status) {
        case 400:
          return new Error(data.message || 'Invalid request, please check your input.')
        case 401:
          return new Error('Please login to continue.')
        case 403:
          return new Error('You do not have permission to perform this action.')
        case 404:
          return new Error('Video not found.')
        case 413:
          return new Error('File size exceeds the maximum limit.')
        case 500:
          return new Error('Server error occurred while processing the video.')
        default:
          return new Error(data.message || 'An unexpected error occurred. Please try again later.')
      }
    }
    // Handle non-HTTP errors (e.g., file validation errors)
    if (error.message) {
      return error
    }
    return new Error('Unable to connect to server. Please check your network connection.')
  }
}

export default VideoService
